// frontend/src/utils/costImportRun.js
//
// What the cost-import page tells the merchant. How a run READS — pending,
// finished, failed, unreadable — is `backgroundRunView` (utils/backgroundRun.js),
// shared with every other run a merchant starts and watches.

import { translateRequestError } from '../queries/api';

/**
 * What to tell the merchant about a failed run.
 *
 * Only the causes they can actually fix get named copy — the backend sends a
 * code for exactly those (`ChannelImportError`) and an empty string for
 * everything else, so a token expiry or a Shopify outage falls through to the
 * generic line instead of being handed an instruction that wouldn't help.
 * Keys are spelled out rather than built from the code so the i18n audit can
 * see them; every locale bundle carries all three.
 */
export function costImportFailureMessage(errorCode, t) {
  if (errorCode === 'store_currency_mismatch') {
    return t('costImport.failure.storeCurrencyMismatch');
  }
  if (errorCode === 'store_currency_missing') {
    return t('costImport.failure.storeCurrencyMissing');
  }
  if (errorCode === 'sales_channel_disconnected') {
    return t('costImport.failure.salesChannelDisconnected');
  }
  return t('costImport.runInterrupted');
}

/**
 * What to tell the merchant when the run could not even be QUEUED.
 *
 * Distinct from `costImportFailureMessage` because the fallbacks differ: a run
 * that started and broke gets "costs already imported have been kept", which
 * would be a lie about a run that never existed.
 *
 * `sales_channel_disconnected` is shared with the mid-run failure: it is the
 * same condition and deserves the same sentence. The backend refuses a start
 * with a code, never a sentence (backend/api/views/cost_import.py). Anything
 * without a named code gets the generic line and the next step for its cause
 * (`translateRequestError`): inventing an instruction for a cause we cannot
 * name is how a merchant ends up changing a setting that was never the
 * problem.
 */
export function costImportStartMessage(error, t) {
  if (error?.code === 'sales_channel_disconnected') {
    return t('costImport.failure.salesChannelDisconnected');
  }
  if (error?.code === 'cost_import_queue_disabled') {
    return t('costImport.failure.queueDisabled');
  }
  return translateRequestError(error, t, 'costImport.runFailed');
}

/**
 * What to tell the merchant about ONE item the import couldn't price.
 *
 * The backend sends a code and its parameters, never a sentence — these rows
 * are rendered into a page that may be in Spanish, Dutch or Polish, and the
 * failure detail behind them (`str(exc)` of a DB or FX error) is not merchant
 * text in any language.
 */
export function costImportItemError(err, t) {
  if (err.error_code === 'fx_unavailable') {
    return t('costImport.itemError.fxUnavailable', {
      from: err.from_currency, to: err.to_currency,
    });
  }
  if (err.error_code === 'push_refused') {
    return t('costImport.itemError.pushRefused');
  }
  return t('costImport.itemError.writeFailed');
}
